// The changes on a page against the live site, cut down to the words that changed. A port of hunks() and pageChanges()
// in bloxx-analytics-mcp src/journey/editPage.ts, so the Review list here reads exactly like the standalone editor's.

import type { DiffResponse } from "./api";

export const COMPONENT_NAMES: Record<string, string> = {
  hero: "Hero", faq: "FAQs", faqs: "FAQs", cta: "Call to action", cards: "Cards", steps: "How it works", lead_form: "Enquiry form",
  footer: "Footer", header: "Header & navigation", nav: "Navigation", reviews: "Reviews", testimonials: "Reviews", pricing: "Pricing",
  stats: "Key facts strip", glance: "Key facts strip", sources: "Sources", related: "Related pages",
};

export const humanComponent = (k: string | null | undefined): string =>
  (k && COMPONENT_NAMES[k]) || (k ? String(k).replace(/[-_:]+/g, " ").replace(/^./, (c) => c.toUpperCase()) : "Block");

export const norm = (t: string | null | undefined): string => (t || "").replace(/\s+/g, " ").trim();

export interface Hunk { pre: string; del: string; ins: string; post: string; tail?: boolean }

const words = (s: string) => norm(s).split(" ").filter(Boolean);

/** Word-level changes between two texts, with up to five words of context either side. */
export function hunks(before: string, after: string): Hunk[] {
  const clipped = /…$/.test(before) && /…$/.test(after);
  const a = words(clipped ? before.slice(0, -1) : before);
  const b = words(clipped ? after.slice(0, -1) : after);
  const out: Hunk[] = [];
  if (clipped) { a.pop(); b.pop(); }
  if (!a.length && !b.length) return out;
  if (a.length * b.length > 250000) {
    let p = 0; while (p < a.length && p < b.length && a[p] === b[p]) p++;
    let q = 0; while (q < a.length - p && q < b.length - p && a[a.length - 1 - q] === b[b.length - 1 - q]) q++;
    return [{ pre: a.slice(Math.max(0, p - 5), p).join(" "), del: a.slice(p, a.length - q).join(" "), ins: b.slice(p, b.length - q).join(" "), post: a.slice(a.length - q, a.length - q + 5).join(" ") }];
  }
  const m = a.length, n = b.length;
  const L = Array.from({ length: m + 1 }, () => new Uint16Array(n + 1));
  for (let i = m - 1; i >= 0; i--) for (let j = n - 1; j >= 0; j--) L[i][j] = a[i] === b[j] ? L[i + 1][j + 1] + 1 : Math.max(L[i + 1][j], L[i][j + 1]);
  const ops: ["=" | "-" | "+", string][] = [];
  let i = 0, j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) { ops.push(["=", a[i]]); i++; j++; }
    else if (L[i + 1][j] >= L[i][j + 1]) ops.push(["-", a[i++]]);
    else ops.push(["+", b[j++]]);
  }
  while (i < m) ops.push(["-", a[i++]]);
  while (j < n) ops.push(["+", b[j++]]);
  let k = 0;
  while (k < ops.length) {
    if (ops[k][0] === "=") { k++; continue; }
    let e = k;
    const del: string[] = [], ins: string[] = [];
    while (e < ops.length) {
      if (ops[e][0] === "-") del.push(ops[e][1]);
      else if (ops[e][0] === "+") ins.push(ops[e][1]);
      else {
        let r = e; while (r < ops.length && ops[r][0] === "=") r++;
        if (r < ops.length && r - e <= 3) { for (let x = e; x < r; x++) { del.push(ops[x][1]); ins.push(ops[x][1]); } e = r; continue; }
        break;
      }
      e++;
    }
    const pre: string[] = [], post: string[] = [];
    for (let x = k - 1; x >= 0 && pre.length < 5 && ops[x][0] === "="; x--) pre.unshift(ops[x][1]);
    for (let x = e; x < ops.length && post.length < 5 && ops[x][0] === "="; x++) post.push(ops[x][1]);
    out.push({ pre: pre.join(" "), del: del.join(" "), ins: ins.join(" "), post: post.join(" "), tail: e >= ops.length });
    k = e;
  }
  return clipped ? out.filter((h) => !h.tail) : out;
}

export type ChangeKind = "Text" | "New page" | "New section" | "Removed section" | "Record updated";

export interface PageChange extends Hunk {
  id: string;
  section: string;
  comp: string;
  kind: ChangeKind;
  /** What to look for in the frame when highlighting this change. */
  hl: string;
  note?: string;
}

export interface IndexedDiff {
  pages: Record<string, DiffResponse["pages"][number]>;
  components: Record<string, Record<string, string>>;
}

export function indexDiff(d: DiffResponse): IndexedDiff {
  return {
    pages: Object.fromEntries((d.pages || []).map((p) => [p.path, p])),
    components: Object.fromEntries(
      Object.entries(d.draft_sections || {}).map(([p, list]) => [p, Object.fromEntries(list.filter((s) => s.component).map((s) => [s.id, s.component as string]))]),
    ),
  };
}

export function pageChanges(diff: IndexedDiff | null, path: string): PageChange[] {
  const d = diff?.pages[path];
  if (!d) return [];
  const comps = diff!.components[path] || {};
  const out: PageChange[] = [];
  for (const s of d.sections) {
    const comp = humanComponent(comps[s.section] || String(s.section).replace(/-\d+$/, ""));
    const base = { section: s.section, comp, pre: "", post: "", hl: "" };
    if (s.change === "added") {
      const after = norm(s.after);
      out.push({ ...base, id: `${path}#${s.section}#a`, kind: "New section", del: "", ins: after.slice(0, 220) + (after.length > 220 ? " …" : "") });
      continue;
    }
    if (s.change === "removed") {
      out.push({ ...base, id: `${path}#${s.section}#r`, kind: "Removed section", del: norm(s.before).slice(0, 220), ins: "" });
      continue;
    }
    const hs = hunks(s.before || "", s.after || "");
    const records = s.records || [];
    if (!hs.length && !records.length) out.push({ ...base, id: `${path}#${s.section}#x`, kind: "Text", del: "", ins: "", note: "Text changed further down this section" });
    if (!hs.length && records.length) out.push({ ...base, id: `${path}#${s.section}#rec`, kind: "Record updated", del: "", ins: records.map((r) => r.record).join(", ") });
    hs.forEach((h, i) => out.push({ ...base, ...h, id: `${path}#${s.section}#${i}`, kind: "Text", hl: h.ins || h.pre.split(" ").slice(-3).join(" ") }));
  }
  return out;
}

/** Two changes are "the same change" when the same words went out and the same words came in. */
export const changeSignature = (c: PageChange): string | null => (c.kind === "Text" ? c.del + "→" + c.ins : null);
